import React, { useState, useEffect } from 'react';
import { Box, Typography, AppBar, Toolbar, Avatar, Button, Grid, Card, CardContent, CardActions, CircularProgress } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Logout as LogoutIcon, Business as BusinessIcon } from '@mui/icons-material';
import { logout } from '../store/slices/authSlice';
import { darkColors, lightColors } from '../theme';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import { createCommonStyles } from '../theme/styles';
import lookupService from '../services/lookupService';
import tenantService from '../services/tenantService';
import Logo from '../assets/ZakazujMeLogo4.png';

const TestPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { isDarkMode } = useAppTheme();
  const customColors = isDarkMode ? darkColors : lightColors;
  const commonStyles = createCommonStyles(isDarkMode);
  
  const [clientTypes, setClientTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClientType, setSelectedClientType] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);

  useEffect(() => {
    fetchClientTypes();
  }, []);

  const fetchClientTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching client types from lookup table...');
      
      // Pozovi specifičnu rutu za tipove klijenata
      const types = await lookupService.getAllLookupValues('_tip_klijenta');
      
      console.log('API Response for _tip_klijenta:', types);
      
      if (types && types.length > 0) {
        console.log('Setting client types from API:', types);
        setClientTypes(types);
      } else {
        console.log('No data from API, using fallback');
        // Fallback podaci - samo vulkanizer i frizer
        setClientTypes([
          { _id: '1', name: 'Vulkanizer', description: 'Servis za vulkanizaciju i popravku guma' },
          { _id: '2', name: 'Frizer', description: 'Frizerski salon za muške i ženske frizure' }
        ]);
      }
    } catch (err) {
      console.error('Error fetching client types:', err);
      setError('Greška pri učitavanju tipova klijenata');
      console.log('Using fallback due to error');
      // Fallback podaci - samo vulkanizer i frizer
      setClientTypes([
        { _id: '1', name: 'Vulkanizer', description: 'Servis za vulkanizaciju i popravku guma' },
        { _id: '2', name: 'Frizer', description: 'Frizerski salon za muške i ženske frizure' }
      ]);
    } finally {
      setLoading(false);
    }
  };

//   const getClientTypeIcon = (typeName) => {
//     const name = typeName.toLowerCase();
//     if (name.includes('vulkanizer')) {
//       return <BuildIcon sx={{ fontSize: 40, color: '#ffffff' }} />;
//     } else if (name.includes('frizer')) {
//       return <ContentCutIcon sx={{ fontSize: 40, color: '#ffffff' }} />;
//     } else {
//       return <BuildIcon sx={{ fontSize: 40, color: '#ffffff' }} />;
//     }
//   };

  const fetchTenantsByClientType = async (clientTypeId) => {
    try {
      setLoadingTenants(true);
      console.log('Fetching tenants for client type:', clientTypeId);
      
      const response = await tenantService.getAllTenants();
      console.log('All tenants response:', response);
      
      const allTenants = response.tenants || response || [];
      // Filtriraj tenant-e po clientType
      const filteredTenants = allTenants.filter(tenant => tenant.clientType === clientTypeId);
      
      console.log('Filtered tenants:', filteredTenants);
      setTenants(filteredTenants);
    } catch (err) {
      console.error('Error fetching tenants:', err);
      setTenants([]);
    } finally {
      setLoadingTenants(false);
    }
  };

  const handleClientTypeSelect = (clientType) => {
    console.log('Selected client type:', clientType);
    setSelectedClientType(clientType);
    fetchTenantsByClientType(clientType._id);
  };

  const handleTenantSelect = (tenant) => {
    console.log('Selected tenant:', tenant);
    // Navigate to appointments page with selected tenant
    navigate('/appointments', { 
      state: { 
        selectedTenantId: tenant._id,
        openCreateModal: true 
      } 
    });
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: customColors.background.default,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <AppBar 
        position="fixed" 
        sx={{ 
          backgroundColor: isDarkMode 
            ? 'rgba(0, 0, 0, 0.8)'
            : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderBottom: isDarkMode 
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)'
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Logo i naslov */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <img 
              src={Logo} 
              alt="Zakazuj" 
              style={{ height: 40, width: 'auto' }}
            />
            <Typography variant="h6" sx={commonStyles.textPrimary}>
              Admin Panel - Test Mode
            </Typography>
          </Box>

          {/* Korisničke informacije i logout */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              backgroundColor: customColors.primary.main,
              width: 32,
              height: 32,
              fontSize: '0.875rem'
            }}>
              {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'A'}
            </Avatar>
            <Typography variant="body2" sx={commonStyles.textSecondary}>
              {user?.email || 'admin@zakazi.com'}
            </Typography>
            <Button
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              sx={{
                color: customColors.text.secondary,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Odjavi se
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main content */}
      <Box sx={{ 
        flexGrow: 1,
        padding: 3,
        paddingTop: 10, // Dodaj prostor za fixed header
        backgroundColor: isDarkMode 
          ? '#1a1a2e' // Plavkasta pozadina za dark mode
          : '#ffffff' // Bela pozadina za light mode
      }}>
        {/* Header sekcija */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" sx={commonStyles.textPrimary} gutterBottom>
            Tipovi Klijenata
          </Typography>
          <Typography variant="h6" sx={commonStyles.textSecondary}>
            Dinamički prikaz tipova klijenata iz lookup tabele
          </Typography>
        </Box>

        {/* Loading state */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress sx={{ color: customColors.primary.main }} />
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" sx={{ color: '#ff6b6b' }}>
              {error}
            </Typography>
          </Box>
        )}

        {/* Kartice sa tipovima klijenata */}
        {!loading && !error && (
          <Grid container spacing={3} justifyContent="center">
            {clientTypes.map((clientType) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={clientType._id}>
                <Card 
                  onClick={() => handleClientTypeSelect(clientType)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: selectedClientType?._id === clientType._id
                      ? (isDarkMode ? '#40407a' : '#e3f2fd') // Highlighted background
                      : (isDarkMode ? '#2c2c54' : '#f8f9fa'), // Normal background
                    border: selectedClientType?._id === clientType._id
                      ? `2px solid ${customColors.primary.main}` // Highlighted border
                      : (isDarkMode ? `1px solid #40407a` : `1px solid #e9ecef`), // Normal border
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDarkMode 
                        ? '0 8px 25px rgba(0, 0, 0, 0.3)'
                        : '0 8px 25px rgba(0, 0, 0, 0.1)',
                      backgroundColor: isDarkMode 
                        ? '#3c3c6a' // Svetlija sivkasta na hover za dark mode
                        : '#ffffff', // Bela na hover za light mode
                    }
                  }}>
                  {/* Ikona i ime */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center',
                    height: 120,
                    backgroundColor: selectedClientType?._id === clientType._id
                      ? customColors.primary.main // Highlighted background
                      : (isDarkMode ? '#40407a' : '#e9ecef'), // Normal background
                    borderBottom: isDarkMode 
                      ? `1px solid #5a5a8a` // Siva granica za dark mode
                      : `1px solid #dee2e6`, // Svetla granica za light mode
                    p: 2
                  }}>
                    <Typography variant="h6" sx={{ 
                      color: selectedClientType?._id === clientType._id 
                        ? '#ffffff' // White text for highlighted
                        : (isDarkMode ? '#ffffff' : '#000000'), // Normal text color
                      mt: 1,
                      textAlign: 'center',
                      fontWeight: 'bold'
                    }}>
                      {clientType.name}
                    </Typography>
                  </Box>

                  {/* Sadržaj kartice */}
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography variant="body2" sx={{ 
                      color: isDarkMode ? '#ffffff' : '#000000', 
                      opacity: isDarkMode ? 0.8 : 0.7 
                    }}>
                      {clientType.description || 'Opis tipa klijenta nije dostupan.'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Prikaz tenant-a za izabrani tip klijenta */}
        {selectedClientType && (
          <Box sx={{ mt: 6 }}>
            <Typography variant="h4" sx={commonStyles.textPrimary} gutterBottom>
              Tenant-i za tip: {selectedClientType.name}
            </Typography>
            
            {loadingTenants && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress sx={{ color: customColors.primary.main }} />
              </Box>
            )}

            {!loadingTenants && (
              <>
                {tenants.length > 0 ? (
                  <Grid container spacing={3} sx={{ mt: 2 }}>
                    {tenants.map((tenant) => (
                      <Grid item xs={12} sm={6} md={4} key={tenant._id}>
                        <Card sx={{
                          backgroundColor: isDarkMode ? '#2c2c54' : '#f8f9fa',
                          border: isDarkMode ? `1px solid #40407a` : `1px solid #e9ecef`,
                          borderRadius: 2,
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            backgroundColor: isDarkMode ? '#3c3c6a' : '#ffffff',
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <BusinessIcon sx={{ 
                                mr: 1, 
                                color: customColors.primary.main,
                                fontSize: 24 
                              }} />
                              <Typography variant="h6" sx={commonStyles.textPrimary}>
                                {tenant.name}
                              </Typography>
                            </Box>
                            
                            <Typography variant="body2" sx={commonStyles.textSecondary} gutterBottom>
                              Email: {tenant.email || 'N/A'}
                            </Typography>
                            
                            <Typography variant="body2" sx={commonStyles.textSecondary} gutterBottom>
                              Telefon: {tenant.phone || 'N/A'}
                            </Typography>
                            
                            {tenant.address && (
                              <Typography variant="body2" sx={commonStyles.textSecondary}>
                                Adresa: {tenant.address.street} {tenant.address.number}, {tenant.address.city}
                              </Typography>
                            )}
                          </CardContent>
                          
                          <CardActions sx={{ p: 2, pt: 0 }}>
                            <Button
                              fullWidth
                              variant="contained"
                              onClick={() => handleTenantSelect(tenant)}
                              sx={{
                                backgroundColor: customColors.primary.main,
                                color: '#ffffff',
                                '&:hover': {
                                  backgroundColor: customColors.primary.dark,
                                },
                                textTransform: 'none',
                                fontWeight: 'bold'
                              }}
                            >
                              Zakazi termin
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: 4, 
                    backgroundColor: isDarkMode ? '#2c2c54' : '#f8f9fa',
                    borderRadius: 2,
                    border: isDarkMode ? `1px solid #40407a` : `1px solid #e9ecef`
                  }}>
                    <Typography variant="h6" sx={commonStyles.textSecondary}>
                      Nema tenant-a za ovaj tip klijenta
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* Footer info */}
        <Box sx={{ 
          mt: 6, 
          p: 3, 
          backgroundColor: isDarkMode 
            ? '#2c2c54' // Sivkasta pozadina za dark mode
            : '#f8f9fa', // Svetla pozadina za light mode
          borderRadius: 2,
          border: isDarkMode 
            ? `1px solid #40407a` // Siva granica za dark mode
            : `1px solid #e9ecef`, // Svetla granica za light mode
          textAlign: 'center'
        }}>
          <Typography variant="h6" sx={commonStyles.textPrimary} gutterBottom>
            Status: ✅ Prikazuje se {clientTypes.length} tipova klijenata
          </Typography>
          <Typography variant="body2" sx={commonStyles.textSecondary}>
            Podaci se učitavaju iz lookup tabele "_tip_klijenta"
          </Typography>
          <Typography variant="body2" sx={{ ...commonStyles.textSecondary, mt: 1, fontSize: '0.75rem' }}>
            Debug: {loading ? 'Učitava se...' : error ? `Greška: ${error}` : 'Podaci učitani'}
          </Typography>
          <Typography variant="body2" sx={{ ...commonStyles.textSecondary, fontSize: '0.75rem' }}>
            Tipovi: {clientTypes.map(t => t.name).join(', ')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TestPage;